import React from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ChildAvatar from '@/components/ChildAvatar';
import { addPdfHeader, getPdfHeaderHeight } from '@/utils/pdfHelper';

const AttendanceGridReport = ({ gridData, reportType }) => {
  if (!gridData || !gridData.report || gridData.report.length === 0) {
    return null;
  }

  // Função para recortar imagem em formato quadrado centralizado
  const cropImageToSquare = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Determinar o tamanho do quadrado (menor dimensão)
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;
        
        // Calcular posição para centralizar o recorte
        const offsetX = (img.width - size) / 2;
        const offsetY = (img.height - size) / 2;
        
        // Desenhar a imagem recortada e centralizada
        ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);
        
        // Retornar como base64
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => resolve(imageDataUrl); // Em caso de erro, usar original
      img.src = imageDataUrl;
    });
  };

  const exportIndividualToPDF = async (child) => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      let startY = addPdfHeader(doc);
      
      // Título
      doc.setFontSize(16);
      doc.text(`Relatorio Individual - ${gridData.period}`, 14, startY);
      
      doc.setFontSize(10);
      doc.text(`Crianca: ${child.child_name}`, 14, startY + 7);
      
      let yPosition = startY + 15;
      const photoSize = 25; // tamanho quadrado em mm
      
      // Adicionar foto se existir (recortada em quadrado)
      if (child.photo_url) {
        try {
          const croppedPhoto = await cropImageToSquare(child.photo_url);
          doc.addImage(croppedPhoto, 'JPEG', 14, yPosition, photoSize, photoSize);
        } catch (e) {
          console.log('Erro ao adicionar foto ao PDF:', e);
        }
      }
      
      // Nome da criança - posicionado ao lado da foto ou no início
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      const childGender = child.gender?.toUpperCase() === 'MASCULINO' ? 'M' : 'F';
      const nameX = child.photo_url ? 14 + photoSize + 5 : 14; // ao lado da foto com 5mm de espaço
      const nameY = child.photo_url ? yPosition + 10 : yPosition; // centralizado verticalmente com a foto
      doc.text(`${child.child_name} - ${child.age} anos (${childGender})`, nameX, nameY);
      
      // Estatísticas - abaixo do nome
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(`Presentes: ${child.present_days} | Ausentes: ${child.absent_days} | Taxa: ${child.attendance_rate}%`, nameX, nameY + 6);
      
      // Avançar yPosition para depois da foto
      if (child.photo_url) {
        yPosition += photoSize + 5;
      } else {
        yPosition += 15;
      }
      
      // Preparar dados da tabela
      const tableData = (child.attendance_grid || []).map(day => {
        const date = new Date(day.date + 'T12:00:00').toLocaleDateString('pt-BR');
        let status = '-';
        
        if (day.is_holiday) {
          status = `FERIADO: ${day.holiday_name}`;
        } else if (day.present === null) {
          status = 'Nao registrado';
        } else if (day.present) {
          status = 'Presente';
        } else if (day.justified) {
          status = `Falta Justificada`;
        } else {
          status = 'Ausente';
        }
        
        return [
          date,
          status,
          day.observations || '-'
        ];
      });
      
      // Criar tabela
      autoTable(doc, {
        startY: yPosition,
        head: [['Data', 'Status', 'Observacoes']],
        body: tableData,
        margin: { left: 14, right: 14, top: 10, bottom: 10 },
        styles: { 
          fontSize: 7, 
          cellPadding: 2,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        headStyles: { 
          fillColor: [37, 99, 235], 
          textColor: 255,
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: 28, halign: 'left' },
          1: { cellWidth: 60, halign: 'left' },
          2: { cellWidth: 'auto', halign: 'left' }
        },
        didParseCell: function(data) {
          if (data.column.index === 1 && data.section === 'body' && child.attendance_grid[data.row.index]) {
            const rowData = child.attendance_grid[data.row.index];
            if (rowData.is_holiday) {
              data.cell.styles.fillColor = [255, 251, 230];
            } else if (rowData.present) {
              data.cell.styles.fillColor = [220, 252, 231];
            } else if (rowData.present === false && !rowData.justified) {
              data.cell.styles.fillColor = [254, 226, 226];
            } else if (rowData.justified) {
              data.cell.styles.fillColor = [255, 237, 213];
            }
          }
        }
      });
      
      const childNameSafe = child.child_name.replace(/[^a-zA-Z0-9]/g, '_');
      doc.save(`Relatorio_Individual_${childNameSafe}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF individual:', error);
      alert('Erro ao gerar PDF. Por favor, tente novamente.');
    }
  };

  const exportToPDF = async () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      let headerY = addPdfHeader(doc);
      
      // Título
      doc.setFontSize(16);
      doc.text(`Relatorio de Frequencia - ${gridData.period}`, 14, headerY);
      
      doc.setFontSize(10);
      doc.text(`Total de criancas: ${gridData.total_children}`, 14, headerY + 7);
      
      // Para cada criança, criar uma tabela
      let yPosition = headerY + 15;
      const photoSize = 20; // tamanho quadrado em mm
      
      for (const child of gridData.report) {
        // Estimar altura necessária (nome + stats + min 5 linhas de tabela = ~40mm)
        const estimatedHeight = child.photo_url ? 55 : 40;
        
        // Verificar se precisa de nova página
        if (yPosition + estimatedHeight > 180) {
          doc.addPage();
          yPosition = addPdfHeader(doc);
        }
        
        const startY = yPosition;
        
        // Adicionar foto se existir (recortada em quadrado)
        if (child.photo_url) {
          try {
            const croppedPhoto = await cropImageToSquare(child.photo_url);
            doc.addImage(croppedPhoto, 'JPEG', 14, yPosition, photoSize, photoSize);
          } catch (e) {
            console.log('Erro ao adicionar foto ao PDF:', e);
          }
        }
        
        // Nome da criança - posicionado ao lado da foto
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        const childName = child.child_name || 'Nome nao disponivel';
        const childGender = child.gender?.toUpperCase() === 'MASCULINO' ? 'M' : 'F';
        const nameX = child.photo_url ? 14 + photoSize + 4 : 14;
        const nameY = child.photo_url ? startY + 8 : startY;
        doc.text(`${childName} - ${child.age} anos (${childGender})`, nameX, nameY);
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(`Presentes: ${child.present_days} | Ausentes: ${child.absent_days} | Taxa: ${child.attendance_rate}%`, nameX, nameY + 5);
        
        // Avançar yPosition para depois da foto
        if (child.photo_url) {
          yPosition = startY + photoSize + 3;
        } else {
          yPosition += 12;
        }
        
        // Preparar dados da tabela
        const tableData = (child.attendance_grid || []).map(day => {
          const date = new Date(day.date + 'T12:00:00').toLocaleDateString('pt-BR');
          let status = '-';
          
          if (day.is_holiday) {
            status = `FERIADO: ${day.holiday_name}`;
          } else if (day.present === null) {
            status = 'Nao registrado';
          } else if (day.present) {
            status = 'Presente';
          } else if (day.justified) {
            status = `Falta Justificada`;
          } else {
            status = 'Ausente';
          }
          
          return [
            date,
            status,
            day.observations || '-'
          ];
        });
        
        // Criar tabela
        autoTable(doc, {
          startY: yPosition,
          head: [['Data', 'Status', 'Observacoes']],
          body: tableData,
          margin: { left: 14, right: 14, top: 10, bottom: 10 },
          styles: { 
            fontSize: 7, 
            cellPadding: 2,
            lineColor: [200, 200, 200],
            lineWidth: 0.1
          },
          headStyles: { 
            fillColor: [37, 99, 235], 
            textColor: 255,
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 28, halign: 'left' },
            1: { cellWidth: 60, halign: 'left' },
            2: { cellWidth: 'auto', halign: 'left' }
          },
          didParseCell: function(data) {
            if (data.column.index === 1 && data.section === 'body' && child.attendance_grid[data.row.index]) {
              const rowData = child.attendance_grid[data.row.index];
              if (rowData.is_holiday) {
                data.cell.styles.textColor = [147, 51, 234];
                data.cell.styles.fontStyle = 'bold';
              } else if (rowData.present === null) {
                data.cell.styles.textColor = [148, 163, 184];
              } else if (rowData.present) {
                data.cell.styles.textColor = [34, 197, 94];
                data.cell.styles.fontStyle = 'bold';
              } else if (rowData.justified) {
                data.cell.styles.textColor = [245, 158, 11];
                data.cell.styles.fontStyle = 'bold';
              } else {
                data.cell.styles.textColor = [239, 68, 68];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
          didDrawPage: function(data) {
            // Atualizar yPosition após cada página
            yPosition = data.cursor.y + 12;
          }
        });
        
        // Garantir espaço adequado após a tabela
        const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : yPosition;
        yPosition = finalY + 12;
      }
      
      // Salvar PDF
      const filename = `Relatorio_Frequencia_${gridData.period.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(filename);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Verifique o console para mais detalhes.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Botão de Exportar */}
      {/* Botão de exportar PDF completo - só para relatório de grupo */}
      {(!reportType || reportType === 'group') && gridData.report.length > 1 && (
        <div className="flex justify-end mb-6">
          <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700">
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF Completo (Todas as Crianças)
          </Button>
        </div>
      )}

      {/* Relatório para cada criança */}
      {gridData.report.map((child) => (
        <div key={child.child_id} className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
          {/* Cabeçalho da criança */}
          <div className="mb-4 pb-4 border-b border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4 flex-1">
                {/* Foto da criança */}
                <ChildAvatar 
                  photo={child.photo_url} 
                  name={child.child_name} 
                  size="xl"
                  className="border-2 border-blue-200"
                />
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-800">{child.child_name}</h3>
                  <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                    <span>{child.age} anos - {child.gender?.toUpperCase() === 'MASCULINO' ? 'Masculino' : 'Feminino'}</span>
                    <span>•</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                      {child.enrollment_origin === 'livre_demanda' ? 'Livre Demanda' :
                       child.enrollment_origin === 'gov_celso_ramos' ? 'Governador Celso Ramos' : 'Biguaçu'}
                    </span>
                    <span>•</span>
                    <span>{child.shift === 'Matutino' ? 'Manhã' : 'Tarde'}</span>
                  </div>
                </div>
              </div>
              
              {/* Botão de PDF individual */}
              <Button 
                onClick={() => exportIndividualToPDF(child)} 
                size="sm"
                className="bg-green-600 hover:bg-green-700 ml-4"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar PDF
              </Button>
            </div>
            
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="bg-green-50 p-2 rounded">
                <p className="text-xs text-slate-600">Presentes</p>
                <p className="text-lg font-bold text-green-600">{child.present_days}</p>
              </div>
              <div className="bg-red-50 p-2 rounded">
                <p className="text-xs text-slate-600">Ausentes</p>
                <p className="text-lg font-bold text-red-600">{child.absent_days}</p>
              </div>
              <div className="bg-orange-50 p-2 rounded">
                <p className="text-xs text-slate-600">Justificadas</p>
                <p className="text-lg font-bold text-orange-600">{child.justified_days}</p>
              </div>
              <div className="bg-blue-50 p-2 rounded">
                <p className="text-xs text-slate-600">Taxa</p>
                <p className="text-lg font-bold text-blue-600">{child.attendance_rate}%</p>
              </div>
            </div>
          </div>

          {/* Grade de frequência */}
          <div className="overflow-x-auto">
            <table className="min-w-full border border-slate-200">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-200 px-3 py-2 text-left text-xs font-medium text-slate-600">
                    Data
                  </th>
                  <th className="border border-slate-200 px-3 py-2 text-center text-xs font-medium text-slate-600">
                    Status
                  </th>
                  <th className="border border-slate-200 px-3 py-2 text-left text-xs font-medium text-slate-600">
                    Observações
                  </th>
                </tr>
              </thead>
              <tbody>
                {child.attendance_grid.map((day, idx) => {
                  let statusClass = '';
                  let statusText = '';
                  let statusBadge = '';
                  
                  if (day.is_holiday) {
                    statusClass = 'bg-purple-50';
                    statusText = day.holiday_name;
                    statusBadge = 'bg-purple-500 text-white';
                  } else if (day.present === null) {
                    statusClass = 'bg-gray-50';
                    statusText = 'Não registrado';
                    statusBadge = 'bg-gray-400 text-white';
                  } else if (day.present) {
                    statusClass = 'bg-green-50';
                    statusText = 'Presente';
                    statusBadge = 'bg-green-500 text-white';
                  } else if (day.justified) {
                    statusClass = 'bg-orange-50';
                    statusText = 'Falta Justificada';
                    statusBadge = 'bg-orange-500 text-white';
                  } else {
                    statusClass = 'bg-red-50';
                    statusText = 'Ausente';
                    statusBadge = 'bg-red-500 text-white';
                  }

                  return (
                    <tr key={idx} className={`hover:bg-slate-100 ${statusClass}`}>
                      <td className="border border-slate-200 px-3 py-2 text-sm">
                        {new Date(day.date + 'T12:00:00').toLocaleDateString('pt-BR', { 
                          weekday: 'short', 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}
                      </td>
                      <td className="border border-slate-200 px-3 py-2 text-center text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge}`}>
                          {statusText}
                        </span>
                        {day.justified && day.justification && (
                          <div className="text-xs text-slate-600 mt-1">
                            {day.justification}
                          </div>
                        )}
                      </td>
                      <td className="border border-slate-200 px-3 py-2 text-sm text-slate-600">
                        {day.observations || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AttendanceGridReport;
